@if (data(); as schedule) {
  @let showAvailability = hasAtleastBasic() && schedule.canEditAvailability;

  <div class="sticky top-0 z-sticky mb-md flex h-14 items-center justify-between bg-surface">
    <sb-date-picker
      [value]="plainDate()"
      [periodType]="'month'"
      [showNavigation]="true"
      [showTodayShortcut]="true"
      (dateSettled)="changeDate($event)"
    />
    <div class="flex gap-2">
      @if (hasAtleastBasic() || canAddShifts()) {
        <button [sbMenuTriggerFor]="actionsMenu.panel" sb-button>
          <sb-icon [name]="'ellipsis-v'" />
          {{ 'Actions' | translate }}
        </button>
        <sb-menu #actionsMenu>
          @if (canAddShifts()) {
            <button sbMenuItem [leadingIcon]="'calendar-add-regular'" (triggered)="onAddShift()">
              {{ 'Add shift' | translate }}
            </button>
          }
          @if (hasAtleastBasic()) {
            <button
              sbMenuItem
              [leadingIcon]="'calendar-2-regular'"
              [description]="'Subscribe to Shiftbase events with your personal calendar' | translate"
              (triggered)="onCalendarSync()"
            >
              {{ 'Add to calendar' | translate }}
            </button>
          }
        </sb-menu>
      }
      @if (hasAtleastBasic() && firstEditableWeek(); as editableWeek) {
        <button sb-button [color]="'primary'" (click)="onEditAvailability(editableWeek)">
          <sb-icon [name]="'plus'" />
          {{ 'Add availability' | translate }}
        </button>
      }
    </div>
  </div>
  <div class="mb-xs overflow-x-auto">
    <!-- The container draws the top and left edges and every cell draws only its own right and
         bottom edge, so no two cells ever paint the same grid line. Tailwind's grid-cols-* tracks
         are minmax(0, 1fr), so a per-cell min-width would overflow rather than hold the column
         open; the container's min-width sets the floor instead (6xl is exactly 8 x 144px). -->
    <div
      class="mb-sm grid border-t border-r-0 border-b-0 border-l border-solid border-default"
      [class.grid-cols-8]="showAvailability"
      [class.min-w-6xl]="showAvailability"
      [class.grid-cols-7]="!showAvailability"
      [class.min-w-5xl]="!showAvailability"
    >
      @for (dayLabel of dayLabels; track dayLabel) {
        <div
          class="border-t-0 border-r border-b border-l-0 border-solid border-default bg-muted px-2xs py-sm text-center text-sm font-semibold text-default"
        >
          {{ dayLabel | translate }}
        </div>
      }
      @if (showAvailability) {
        <div
          class="overflow-hidden border-t-0 border-r border-b border-l-0 border-solid border-default bg-muted p-sm text-center text-sm font-semibold text-default"
        >
          {{ 'Availability' | translate }}
        </div>
      }

      @for (week of schedule.weeks; track $index) {
        @for (day of week.days; track $index) {
          <div
            class="flex min-h-22 flex-col justify-between border-t-0 border-r border-b border-l-0 border-solid border-default p-2xs"
            [class.bg-surface-page]="day.isInSelectedMonth"
            [class.bg-muted]="!day.isInSelectedMonth"
            [class.outline-2]="day.isToday"
            [class.outline-brand]="day.isToday"
            [class.-outline-offset-2]="day.isToday"
          >
            <div class="flex flex-initial flex-row content-start text-subtle" [class.font-semibold]="day.isToday">
              <div class="flex grow-10 flex-col self-start overflow-hidden">
                @if (hasAtleastBasic() && day.availabilities.length) {
                  @if (week.canEditAvailability) {
                    @let dayLabel = day.date | localeDate: 'compact';
                    <!-- One control per day: the chips below are a read-out of the same week, and a
                         button per record would put identical tab stops on a split day. The chip
                         clips at every level above it, so the focus ring is drawn inside the box. -->
                    <button
                      type="button"
                      class="reset-button flex w-full cursor-pointer flex-col text-left focus-visible:-outline-offset-1"
                      [attr.aria-label]="'Edit availability for [date]' | translate: { date: dayLabel }"
                      (click)="onEditAvailability(week)"
                    >
                      <ng-container [ngTemplateOutlet]="availabilityChips" />
                    </button>
                  } @else {
                    <ng-container [ngTemplateOutlet]="availabilityChips" />
                  }
                }

                <ng-template #availabilityChips>
                  @for (available of day.availabilities; track available.id) {
                    @if (available.type) {
                      @let isAvailable =
                        available.type === AvailabilityType.AVAILABLE_ALL_DAY ||
                        available.type === AvailabilityType.AVAILABLE_FROM;

                      <div
                        class="ab-tl static mb-sm w-full rounded-full pl-1"
                        [class.bg-success-subtle]="isAvailable"
                        [class.bg-alt]="!isAvailable"
                        [sbTooltip]="tooltipTpl"
                      >
                        <div class="ab-tl__static">
                          <div
                            class="ab-tl__static--content"
                            [class.text-success-strong]="isAvailable"
                            [class.text-default]="!isAvailable"
                          >
                            <div class="flex w-full items-center justify-between truncate">
                              @if (available.type === 'Available from') {
                                <span
                                  >{{ available.starttime | time }} - {{ available.endtime | time }}
                                  {{ 'Available' | translate }}</span
                                >
                              }
                              @if (available.type === 'Unavailable from') {
                                <span
                                  >{{ available.starttime | time }} - {{ available.endtime | time }}
                                  {{ 'Unavailable' | translate }}</span
                                >
                              }
                              @if (available.type === 'Available all day' || available.type === 'Unavailable all day') {
                                <span>
                                  {{ available.type | translate }}
                                </span>
                              }
                              @if (available?.reason) {
                                <sb-icon size="xs" [name]="'chat-1-regular'" />
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <ng-template #tooltipTpl>
                      <div class="relative">
                        <span>{{ available.type | translate }}</span>
                        {{ available.starttime | time }}
                        @if (available.endtime) {
                          - {{ available.endtime | time }}
                        }
                        @if (available.reason) {
                          <div>
                            {{ available.reason }}
                          </div>
                        }
                      </div>
                    </ng-template>
                  }
                </ng-template>
              </div>

              <div class="ml-xs shrink text-right">{{ day.date | localeDate: 'day' }}</div>
            </div>
            <div class="flex flex-1 flex-col justify-end gap-0.5">
              @if (day.formattedDate) {
                @for (absence of day.absences; track absence.id) {
                  <absence-item [absence]="absence" [date]="day.formattedDate" [compactView]="false" />
                }
              }
              @for (shift of day.schedules; track shift.id) {
                @let shiftColor = shift.Shift?.color ?? defaultItemColor;
                <div
                  class="sh-tl group/shift rounded-lg border-2 border-solid text-default"
                  [class.opacity-50]="day.isInPast"
                  [class.sh-tl__margin]="day.schedules?.length > 1"
                  [style.background-color]="shiftColor | userColorBg"
                  [style.border-color]="shiftColor"
                  [sbTooltip]="tp"
                  [sbTooltipPlacement]="'top'"
                >
                  <div class="relative top-0 left-0">
                    <div
                      class="absolute top-[2px] ml-[2px] h-[26px] w-[6px] min-w-[6px] rounded-full"
                      [style.background-color]="shiftColor"
                    ></div>
                  </div>
                  <ng-template #tp>
                    <div class="flex flex-col text-left">
                      <p class="m-0">
                        <strong>{{ shift.Shift.long_name }}</strong
                        >&nbsp;{{ shift.starttime | time }}
                        @if (!shift.hide_end_time) {
                          - {{ shift.endtime | time }}
                        }
                      </p>
                      <p class="m-0">
                        <strong>{{ shift.department_name }}</strong
                        >&nbsp;|&nbsp;<strong>{{ shift.Team.name }}</strong>
                      </p>
                      @if (shift.description) {
                        <p class="m-0">
                          <strong>{{ 'Description:' | translate }}</strong
                          >&nbsp;
                          <span>{{ shift.description }}</span>
                        </p>
                      }
                      @if (day.schedules?.length > 1) {
                        <p class="m-0">
                          <span>{{ 'Employee has already been scheduled' | translate }}</span>
                        </p>
                      }
                    </div>
                  </ng-template>

                  <div class="sh-tl__static relative">
                    <div class="sh-tl__static--content relative -top-px ml-3 p-0 pr-2">
                      <div class="sh-tl__static--shift">
                        <span class="sh-tl__static--shift-name overflow-hidden text-[12px] font-bold text-ellipsis">
                          {{ shift.Shift.long_name }}
                        </span>
                        <span class="sh-tl__static--shift-time overflow-hidden text-xs text-ellipsis">
                          {{ shift.starttime | time }}
                          @if (!shift.hide_end_time) {
                            - {{ shift.endtime | time }}
                          }
                        </span>
                      </div>
                      @if (shift.Shift.is_task && !shift.loading) {
                        <div class="sh-tl__static--icon p-0">
                          <sb-icon name="task-2-regular" class="sh-tl__btn-icon" />
                        </div>
                      }

                      @if (hasAtleastBasic() && shift.Exchange) {
                        <div class="sh-tl__static--icon p-0">
                          <sb-icon name="transfer-3-regular" class="sh-tl__btn-icon" />
                        </div>
                      }
                    </div>
                    <!-- For now we align it 1px off since the sizing does not fully match what we have in the design. This is a temporary solution until we can fix the sizing issue in the design. -->
                    <sb-schedule-item-actions
                      class="absolute inset-x-px top-1/2 -translate-y-1/2 transform opacity-0 transition-opacity group-hover/shift:opacity-100 focus-within:opacity-100"
                      [canEdit]="!!shift.canEdit"
                      [canDelete]="!!shift.canDelete"
                      (edit)="onEditShift(shift)"
                      (view)="onViewShift(shift)"
                      (delete)="onDeleteShift(shift)"
                    />
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if (showAvailability) {
          <div
            class="flex min-h-22 flex-col justify-center border-t-0 border-r border-b border-l-0 border-solid border-default bg-surface-page p-sm"
          >
            <div class="flex items-center justify-center">
              @let weekLabel = week.days[0].date | localeDate: 'compact';
              @if (week.canEditAvailability) {
                <button
                  sb-icon-button
                  color="green"
                  [attr.aria-label]="'Edit availability for the week of [date]' | translate: { date: weekLabel }"
                  (click)="onEditAvailability(week)"
                >
                  <sb-icon name="check-regular" />
                </button>
              } @else {
                @let lockedMessage =
                  'Availability for the week of [date] can no longer be edited' | translate: { date: weekLabel };
                <!-- A disabled sb-icon-button is neither focusable nor able to host a tooltip, so
                     the wrapper carries the role, the name and the tooltip; the button below is
                     visual only, and aria-hidden is safe on it because disabled makes it
                     unfocusable. -->
                <span
                  class="flex"
                  role="button"
                  tabindex="0"
                  aria-disabled="true"
                  [attr.aria-label]="lockedMessage"
                  [sbTooltip]="lockedMessage"
                >
                  <button sb-icon-button color="red" [disabled]="true" aria-hidden="true">
                    <sb-icon name="forbid-circle-regular" />
                  </button>
                </span>
              }
            </div>
          </div>
        }
      }
    </div>
  </div>
} @else {
  <sb-loading-state class="h-full" />
}
